"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import { useMotionValue, useSpring, motion } from "motion/react";
import { usePathname } from "next/navigation";
import type { VariantProps } from "class-variance-authority";
import type { SubMenuItem as SubMenuItemType } from "@/types";
import { Icon } from "@/components/ui/Icon";
import { Link } from "@/components/ui/Link";
import { menuItemVariants } from "./MenuItem";
import { MenuSectionAlign, MenuSectionIndicator } from "@/types/menu";
export interface SubMenuSectionProps {
  /** Sub-menu item data (section with headline, text, and links) */
  item: SubMenuItemType;
  /** Visual variant */
  variant?: VariantProps<typeof menuItemVariants>["variant"];
  isResponsiveWidth?: boolean;
}

/**
 * Sub-menu item component representing a section within a sub-menu.
 * Renders a vertical track on the left with an animated pill that follows
 * the hovered or active link, fading out when neither applies.
 */
export const SubMenuSection: React.FC<SubMenuSectionProps> = t0 => {
  const $ = _c(31);
  const {
    item,
    isResponsiveWidth: t1
  } = t0;
  const isResponsiveWidth = t1 === undefined ? true : t1;
  const {
    indicator: t2,
    align: t3
  } = item;
  const indicator = t2 === undefined ? MenuSectionIndicator.Pill : t2;
  const align = t3 === undefined ? MenuSectionAlign.Top : t3;
  const pathname = usePathname();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const itemRefs = useRef(t4);
  const trackRef = useRef(null);
  const isFirstPositionSet = useRef(false);
  const topMV = useMotionValue(0);
  const heightMV = useMotionValue(0);
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      stiffness: 500,
      damping: 38
    };
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const pillTop = useSpring(topMV, t5);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      stiffness: 500,
      damping: 38
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const pillHeight = useSpring(heightMV, t6);
  let t7;
  if ($[3] !== item.links || $[4] !== pathname) {
    let t8;
    if ($[6] !== pathname) {
      t8 = link => pathname === link.href;
      $[6] = pathname;
      $[7] = t8;
    } else {
      t8 = $[7];
    }
    t7 = item.links.findIndex(t8);
    $[3] = item.links;
    $[4] = pathname;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const activeIndex = t7;
  const targetIndex = hoveredIndex ?? activeIndex;
  let t8;
  let t9;
  if ($[8] !== heightMV || $[9] !== pillHeight || $[10] !== pillTop || $[11] !== targetIndex || $[12] !== topMV) {
    t8 = () => {
      if (targetIndex < 0) {
        return;
      }
      const el = itemRefs.current[targetIndex];
      const track = trackRef.current;
      if (!el || !track) {
        return;
      }
      const top = el.getBoundingClientRect().top - track.getBoundingClientRect().top;
      const height = el.getBoundingClientRect().height;
      if (!isFirstPositionSet.current) {
        pillTop.jump(top);
        pillHeight.jump(height);
        isFirstPositionSet.current = true;
      } else {
        topMV.set(top);
        heightMV.set(height);
      }
    };
    t9 = [targetIndex, topMV, heightMV, pillTop, pillHeight];
    $[8] = heightMV;
    $[9] = pillHeight;
    $[10] = pillTop;
    $[11] = targetIndex;
    $[12] = topMV;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useLayoutEffect(t8, t9);
  const t10 = "flex flex-row gap-sm justify-start items-stretch text-white " + (isResponsiveWidth ? "max-w-80" : " w-56");
  let t11;
  if ($[15] !== indicator || $[16] !== pillHeight || $[17] !== pillTop || $[18] !== targetIndex) {
    t11 = indicator === MenuSectionIndicator.Pill && <div ref={trackRef} className="relative h-full w-0.5 flex-none bg-white/30 rounded-full"><motion.div className="absolute w-full bg-white rounded-full" style={{
        top: pillTop,
        height: pillHeight
      }} animate={{
        opacity: targetIndex >= 0 ? 1 : 0
      }} transition={{
        type: "spring",
        stiffness: 500,
        damping: 38
      }} /></div>;
    $[15] = indicator;
    $[16] = pillHeight;
    $[17] = pillTop;
    $[18] = targetIndex;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = "h-full flex flex-col gap-sm" + (align === MenuSectionAlign.Center ? " justify-center" : "");
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setHoveredIndex(null);
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== activeIndex || $[22] !== item.links) {
    t14 = item.links.map((link_0, index) => <li key={index} ref={el_0 => {
      itemRefs.current[index] = el_0;
    }} onMouseEnter={() => setHoveredIndex(index)} className="my-0"><div aria-hidden="true" className="flex font-medium h-0 overflow-hidden invisible pointer-events-none select-none items-center gap-xs">{link_0.icon && <Icon name={link_0.icon} size={16} />}<span>{link_0.label}</span></div><Link {...link_0} color="text" iconColor="purple" className={"hover:font-medium" + (index === activeIndex ? " font-medium" : "")} /></li>);
    $[21] = activeIndex;
    $[22] = item.links;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t12 || $[25] !== t14) {
    t15 = <div className="h-full"><ul className={t12} onMouseLeave={t13}>{t14}</ul></div>;
    $[24] = t12;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t15) {
    t16 = <div className={t10}>{t11}{t15}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
};